import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import api from '../api';

const MESES_FULL = ['', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const MESES_SHORT = ['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

function fmtCLP(val) {
  if (!val && val !== 0) return '—';
  return '$' + Number(val).toLocaleString('es-CL');
}
function parseMonto(v) {
  if (typeof v === 'number') return v;
  if (!v) return 0;
  return parseInt(String(v).replace(/\./g, '').replace(/[^0-9-]/g, ''), 10) || 0;
}
function parseDocs(v) {
  if (typeof v === 'number') return v;
  return parseInt(v, 10) || 0;
}

function EstadoBadge({ estado, ok, sinDatos, logId, onCambiarEstado }) {
  const getBadge = () => {
    if (estado === 'en_cola') return <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200">⏳ En Cola</span>;
    if (estado === 'en_progreso') return <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 border border-yellow-200">⟳ Procesando</span>;
    if (estado === 'completado') return <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200">✓ OK</span>;
    if (estado === 'error') return <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200">✗ Error</span>;
    
    // Fallbacks
    if (sinDatos) return <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">Sin datos</span>;
    if (ok === true)  return <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200">✓ OK</span>;
    if (ok === false) return <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200">✗ Error</span>;
    return <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-200">—</span>;
  };

  return (
    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      {getBadge()}
      {logId && onCambiarEstado && (
        <select
          value=""
          onChange={(e) => {
            if(e.target.value) onCambiarEstado(logId, e.target.value);
          }}
          className="text-xs bg-transparent border-none text-gray-400 hover:text-gray-700 cursor-pointer appearance-none text-center p-0 outline-none"
          title="Forzar cambio de estado"
        >
          <option value="">⚙️</option>
          <option value="en_cola">Forzar: En cola</option>
          <option value="en_progreso">Forzar: En progreso</option>
          <option value="completado">Forzar: Completado</option>
          <option value="error">Forzar: Error</option>
          <option value="cancelado">Forzar: Cancelado</option>
        </select>
      )}
    </div>
  );
}

function TablaResumenSII({ resumenSii, logData }) {
  let rows = resumenSii || [];
  if (logData && logData.tipoDocumento === 'honorarios' && logData.honorarios?.totalBruto) {
    if (rows.length === 0) {
      rows = [{ 
        tipoDocumento: 'Honorarios',
        totalBruto: logData.honorarios.totalBruto,
        totalRetenido: logData.honorarios.totalRetenido,
        totalPagado: logData.honorarios.totalPagado
      }];
    }
  }

  if (!rows || rows.length === 0) return <p className="text-xs text-gray-400 italic py-3 text-center">Sin datos de resumen SII</p>;
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="w-full text-xs whitespace-nowrap">
        <thead className="bg-gray-50 text-gray-500 uppercase tracking-wide font-semibold border-b">
          <tr>
            <th className="px-3 py-2 text-left">Tipo de Documento</th>
            {logData?.tipoDocumento === 'honorarios' ? (
              <>
                <th className="px-3 py-2 text-right">Brutos</th>
                <th className="px-3 py-2 text-right">Retenido</th>
                <th className="px-3 py-2 text-right font-bold text-gray-700">Pagado</th>
              </>
            ) : (
              <>
                <th className="px-3 py-2 text-right">Cant.</th>
                <th className="px-3 py-2 text-right">M. Exento</th>
                <th className="px-3 py-2 text-right">M. Neto</th>
                <th className="px-3 py-2 text-right">M. IVA</th>
                <th className="px-3 py-2 text-right font-bold text-gray-700">Total</th>
              </>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row, i) => {
            const esBoleta = row.tipoDocumento && (row.tipoDocumento.toUpperCase().includes('BOLETA') || row.tipoDocumento.toUpperCase().includes('COMPROBANTE PAGO'));
            return (
              <tr key={i} className={`hover:bg-gray-50 transition-colors ${esBoleta ? 'bg-indigo-50/50' : ''}`}>
                <td className="px-3 py-2 text-gray-700 font-medium">
                  {esBoleta && <span className="inline-block mr-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 align-middle" />}
                  {row.tipoDocumento}
                </td>
                {logData?.tipoDocumento === 'honorarios' ? (
                  <>
                    <td className="px-3 py-2 text-right text-gray-500 font-mono">{row.totalBruto || row.montoNeto || '0'}</td>
                    <td className="px-3 py-2 text-right text-gray-500 font-mono">{row.totalRetenido || row.montoIva || '0'}</td>
                    <td className="px-3 py-2 text-right text-gray-800 font-semibold font-mono">{row.totalPagado || row.montoTotal || '0'}</td>
                  </>
                ) : (
                  <>
                    <td className="px-3 py-2 text-right text-gray-600 font-mono">{row.totalDocumentos || row.cantidadDocumentos || 0}</td>
                    <td className="px-3 py-2 text-right text-gray-500 font-mono">{row.montoExento || '0'}</td>
                    <td className="px-3 py-2 text-right text-gray-500 font-mono">{row.montoNeto || '0'}</td>
                    <td className="px-3 py-2 text-right text-gray-500 font-mono">{row.montoIva || '0'}</td>
                    <td className="px-3 py-2 text-right text-gray-800 font-semibold font-mono">{row.montoTotal || '0'}</td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function FilaMes({ mesNum, logCompras, logVentas, logCent, logHono, logBte, descargandoArchivo, onDescargar, onReejecutar, onCambiarEstado }) {
  const [expandido, setExpandido] = useState(false);

  const totalCompras = (logCompras?.resumenSii || []).reduce((s, r) => s + parseMonto(r.montoTotal), 0);
  const totalVentas  = (logVentas?.resumenSii  || []).reduce((s, r) => s + parseMonto(r.montoTotal), 0);
  const totalHono    = logHono?.honorarios?.totalBruto || 0;
  const totalBte     = logBte?.bte?.totalBruto || 0;
  const tieneAlgo    = logCompras || logVentas || logCent || logHono || logBte;
  const enProgreso   = logCompras?.estado === 'en_progreso' || logVentas?.estado === 'en_progreso' || logCent?.estado === 'en_progreso' || logHono?.estado === 'en_progreso' || logBte?.estado === 'en_progreso';
  const enCola       = logCompras?.estado === 'en_cola'     || logVentas?.estado === 'en_cola' || logCent?.estado === 'en_cola' || logHono?.estado === 'en_cola' || logBte?.estado === 'en_cola';

  return (
    <div className={`border rounded-xl overflow-hidden transition-shadow ${expandido ? 'shadow-md' : 'shadow-sm'}`}>
      {/* Cabecera de la fila */}
      <button
        onClick={() => tieneAlgo && setExpandido(v => !v)}
        disabled={!tieneAlgo}
        className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
          tieneAlgo ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default bg-gray-50/50'
        } ${enProgreso ? 'bg-yellow-50' : (enCola ? 'bg-blue-50/50' : '')}`}
      >
        {/* Mes */}
        <span className="w-32 font-semibold text-gray-800 text-sm flex-shrink-0">{MESES_FULL[mesNum]}</span>

        {/* Estado compras */}
        <div className="flex items-center gap-2 w-40 flex-shrink-0">
          <span className="text-xs text-gray-400 hidden sm:inline">Compras</span>
          {logCompras
            ? <EstadoBadge estado={logCompras.estado} ok={logCompras.descargaSii?.sinDatos ? undefined : logCompras.subidaInputsoft?.ok} sinDatos={logCompras.descargaSii?.sinDatos} logId={logCompras._id} onCambiarEstado={(id, est) => onCambiarEstado(id, est, 'compras', mesNum)} />
            : <span className="text-xs text-gray-300">—</span>
          }
        </div>

        {/* Estado ventas */}
        <div className="flex items-center gap-2 w-40 flex-shrink-0">
          <span className="text-xs text-gray-400 hidden sm:inline">Ventas</span>
          {logVentas
            ? <EstadoBadge estado={logVentas.estado} ok={logVentas.descargaSii?.sinDatos ? undefined : logVentas.subidaInputsoft?.ok} sinDatos={logVentas.descargaSii?.sinDatos} logId={logVentas._id} onCambiarEstado={(id, est) => onCambiarEstado(id, est, 'ventas', mesNum)} />
            : <span className="text-xs text-gray-300">—</span>
          }
        </div>

        {/* Estado honorarios */}
        <div className="flex items-center gap-2 w-40 flex-shrink-0 hidden lg:flex">
          <span className="text-xs text-gray-400 hidden sm:inline">Hono.</span>
          {logHono
            ? <EstadoBadge estado={logHono.estado} ok={logHono.honorarios?.ok} sinDatos={logHono.honorarios?.sinDatos} logId={logHono._id} onCambiarEstado={(id, est) => onCambiarEstado(id, est, 'honorarios', mesNum)} />
            : <span className="text-xs text-gray-300">—</span>
          }
        </div>

        {/* Estado BTE */}
        <div className="flex items-center gap-2 w-40 flex-shrink-0 hidden lg:flex">
          <span className="text-xs text-gray-400 hidden sm:inline">BTE</span>
          {logBte
            ? <EstadoBadge estado={logBte.estado} ok={logBte.bte?.ok} sinDatos={logBte.bte?.sinDatos} logId={logBte._id} onCambiarEstado={(id, est) => onCambiarEstado(id, est, 'bte', mesNum)} />
            : <span className="text-xs text-gray-300">—</span>
          }
        </div>

        {/* Estado centralización */}
        <div className="flex items-center gap-2 w-40 flex-shrink-0 hidden md:flex">
          <span className="text-xs text-gray-400 hidden sm:inline">Cent.</span>
          {logCent || logCompras?.centralizacionInputsoft || logVentas?.centralizacionInputsoft
            ? (() => {
                const centLog = logCent || (logCompras?.centralizacionInputsoft ? logCompras : logVentas);
                const ok = centLog?.centralizacionInputsoft?.ok;
                const estado = logCent ? logCent.estado : (centLog?.estado === 'en_progreso' ? 'en_progreso' : (ok !== undefined ? 'finalizado' : centLog?.estado));
                return <EstadoBadge estado={estado} ok={ok} logId={centLog?._id} onCambiarEstado={(id, est) => onCambiarEstado(id, est, 'centralizacion', mesNum)} />;
              })()
            : <span className="text-xs text-gray-300">—</span>
          }
        </div>

        {/* Totales */}
        <div className="ml-auto flex items-center gap-6 text-right flex-shrink-0">
          {totalCompras > 0 && (
            <div>
              <div className="text-xs text-gray-400">Compras</div>
              <div className="text-sm font-semibold text-green-700">{fmtCLP(totalCompras)}</div>
            </div>
          )}
          {totalVentas > 0 && (
            <div>
              <div className="text-xs text-gray-400">Ventas</div>
              <div className="text-sm font-semibold text-blue-700">{fmtCLP(totalVentas)}</div>
            </div>
          )}
          {totalHono > 0 && (
            <div>
              <div className="text-xs text-gray-400">Honorarios</div>
              <div className="text-sm font-semibold text-purple-700">{fmtCLP(totalHono)}</div>
            </div>
          )}
          {totalBte > 0 && (
            <div>
              <div className="text-xs text-gray-400">BTE</div>
              <div className="text-sm font-semibold text-teal-700">{fmtCLP(totalBte)}</div>
            </div>
          )}
          {tieneAlgo && (
            <span className={`text-gray-400 transition-transform ${expandido ? 'rotate-180' : ''} text-xs ml-2`}>▼</span>
          )}
          {enProgreso && <span className="text-xs text-yellow-600 animate-pulse">⟳ En progreso</span>}
          {!tieneAlgo && <span className="text-xs text-gray-300 italic">Sin ejecución</span>}
        </div>
      </button>

      {/* Detalle expandido */}
      {expandido && (
        <div className="border-t bg-white px-4 py-4 space-y-6">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {[
              { tipo: 'compras', log: logCompras, color: 'green' },
              { tipo: 'ventas',  log: logVentas,  color: 'blue'  },
              { tipo: 'centralizacion', log: logCent, color: 'amber' },
              { tipo: 'honorarios', log: logHono, color: 'purple' },
              { tipo: 'bte', log: logBte, color: 'teal' }
            ].map(({ tipo, log, color }) => (
              <div key={tipo}>
                <h4 className={`text-sm font-semibold capitalize mb-3 flex items-center gap-2 ${color === 'green' ? 'text-green-700' : color === 'blue' ? 'text-blue-700' : color === 'purple' ? 'text-purple-700' : color === 'teal' ? 'text-teal-700' : 'text-amber-700'}`}>
                  <div className="flex items-center gap-2 flex-1">
                    <span className={`w-2 h-2 rounded-full ${color === 'green' ? 'bg-green-500' : color === 'blue' ? 'bg-blue-500' : color === 'purple' ? 'bg-purple-500' : color === 'teal' ? 'bg-teal-500' : 'bg-amber-500'}`} />
                    {tipo === 'centralizacion' ? 'Centralización' : tipo === 'bte' ? 'BTE' : tipo}
                    {log && <span className="font-normal text-gray-400 text-xs ml-1">— {new Date(log.fechaInicio).toLocaleString('es-CL')}</span>}
                  </div>
                  {(!log || (log.estado !== 'en_progreso' && log.estado !== 'en_cola')) && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onReejecutar(mesNum, tipo); }}
                      className="px-2 py-1 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded text-[10px] uppercase font-bold tracking-wider shadow-sm transition-colors"
                    >
                      ▶ {log ? 'Re-ejecutar' : 'Ejecutar'}
                    </button>
                  )}
                </h4>

                {!log ? (
                  <p className="text-xs text-gray-400 italic">Sin registro de pipeline</p>
                ) : (
                  <div className="space-y-3">
                    {/* Etapas */}
                    <div className="flex flex-wrap gap-2">
                      {[
                        ['descargaSii', 'SII'],
                        ['procesamientoCsv', 'CSV'],
                        ['subidaInputsoft', 'Inputsoft'],
                        ['ingresoBoletas', 'Boletas'],
                        ['centralizacionInputsoft', 'Centralización']
                      ].map(([key, label]) => {
                        const etapa = log[key];
                        if (!etapa) return null;
                        return (
                          <span key={key} className={`text-xs px-2 py-1 rounded font-medium ${
                            etapa.ok ? 'bg-green-100 text-green-700' :
                            etapa.ok === false ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-500'
                          }`}>
                            {etapa.ok ? '✓' : etapa.ok === false ? '✗' : '…'} {label}
                            {etapa.sinDatos ? ' (sin datos)' : ''}
                            {etapa.filasProcesadas != null ? ` (${etapa.filasProcesadas} f.)` : ''}
                          </span>
                        );
                      })}
                    </div>

                    {/* Tabla SII */}
                    <TablaResumenSII resumenSii={log.resumenSii} logData={log} />

                    {/* Errores Inputsoft / Boletas */}
                    {(log.subidaInputsoft?.error || log.subidaInputsoft?.detallesError?.length > 0 || log.ingresoBoletas?.error) && (
                      <div className="mb-2">
                        <h5 className="text-xs font-semibold text-orange-600 mb-1.5 flex items-center gap-1">
                          ⚠ Detalles del Error / Rechazo
                        </h5>
                        
                        {log.subidaInputsoft?.error && (
                          <div className="text-xs text-red-700 bg-red-50 p-2 border border-red-200 rounded mb-2">
                            <span className="font-semibold">Subida CSV:</span> {log.subidaInputsoft.error}
                          </div>
                        )}

                        {log.ingresoBoletas?.error && (
                          <div className="text-xs text-red-700 bg-red-50 p-2 border border-red-200 rounded mb-2">
                            <span className="font-semibold">Ingreso Boletas:</span> {log.ingresoBoletas.error}
                          </div>
                        )}

                        {log.subidaInputsoft?.detallesError?.length > 0 && (
                          <div className="max-h-32 overflow-y-auto border border-orange-200 rounded">
                            <table className="w-full text-xs">
                              <thead className="bg-orange-50 text-orange-700 sticky top-0">
                                <tr>
                                  <th className="p-2 text-left font-medium">Tipo</th>
                                  <th className="p-2 text-left font-medium">Folio</th>
                                  <th className="p-2 text-left font-medium">Motivo</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-orange-100">
                                {log.subidaInputsoft.detallesError.map((e, i) => (
                                  <tr key={i}>
                                    <td className="p-2 text-gray-500">{e.tipo || '—'}</td>
                                    <td className="p-2 font-mono text-gray-700">{e.folio}</td>
                                    <td className="p-2 text-orange-700">{e.motivo}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Resultados Centralización */}
                    {log.centralizacionInputsoft?.resultados?.length > 0 && (
                      <div>
                        <h5 className="text-xs font-semibold text-blue-600 mb-1.5">
                          ℹ Resultados Centralización
                        </h5>
                        <div className="max-h-32 overflow-y-auto border border-blue-200 rounded">
                          <table className="w-full text-xs">
                            <thead className="bg-blue-50 text-blue-700 sticky top-0">
                              <tr>
                                <th className="p-2 text-left font-medium">Tipo</th>
                                <th className="p-2 text-right font-medium">Debe</th>
                                <th className="p-2 text-right font-medium">Haber</th>
                                <th className="p-2 text-left font-medium">Estado</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-blue-100">
                              {log.centralizacionInputsoft.resultados.map((r, i) => (
                                <tr key={i}>
                                  <td className="p-2 text-gray-700 font-medium">{
                                    r.tipoDocumento === '1' ? 'Ventas (1)' : 
                                    r.tipoDocumento === '2' ? 'Compras (2)' : 
                                    r.tipoDocumento === '3' ? 'Boletas (3)' : 
                                    r.tipoDocumento === '4' ? 'Honorarios (4)' : r.tipoDocumento
                                  }</td>
                                  <td className="p-2 text-right font-mono text-gray-600">{r.debe != null ? r.debe.toLocaleString('es-CL') : '—'}</td>
                                  <td className="p-2 text-right font-mono text-gray-600">{r.haber != null ? r.haber.toLocaleString('es-CL') : '—'}</td>
                                  <td className="p-2 text-gray-600">
                                    {r.ok ? <span className="text-green-600">✓ {r.mensaje || 'OK'}</span> : <span className="text-red-600">✗ {r.error}</span>}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Archivos */}
                    <div className="flex flex-wrap gap-2">
                      {log.descargaSii?.archivo && (() => {
                        const fname = log.descargaSii.archivo.split(/[/\\]/).pop();
                        return (
                          <button onClick={() => onDescargar(log._id, fname)}
                            disabled={descargandoArchivo === fname}
                            className="inline-flex items-center gap-1.5 text-xs border rounded-lg px-3 py-1.5 bg-gray-50 hover:bg-gray-100 text-gray-700 disabled:opacity-50 transition-colors">
                            📄 {descargandoArchivo === fname ? 'Descargando...' : 'CSV Original SII'}
                          </button>
                        );
                      })()}
                      {log.procesamientoCsv?.archivo && (() => {
                        const fname = log.procesamientoCsv.archivo.split(/[/\\]/).pop();
                        return (
                          <button onClick={() => onDescargar(log._id, fname)}
                            disabled={descargandoArchivo === fname}
                            className="inline-flex items-center gap-1.5 text-xs border border-blue-200 rounded-lg px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 disabled:opacity-50 transition-colors">
                            📊 {descargandoArchivo === fname ? 'Descargando...' : 'CSV Procesado'}
                          </button>
                        );
                      })()}
                      {/* Archivos de Error Inputsoft */}
                      {(log.subidaInputsoft?.error || log.subidaInputsoft?.ok === false || log.ingresoBoletas?.error || log.ingresoBoletas?.ok === false) && (
                        <>
                          <button onClick={() => onDescargar(log._id, 'error-subida.png')}
                            disabled={descargandoArchivo === 'error-subida.png'}
                            className="inline-flex items-center gap-1.5 text-xs border border-orange-200 rounded-lg px-3 py-1.5 bg-orange-50 hover:bg-orange-100 text-orange-700 disabled:opacity-50 transition-colors">
                            🖼 {descargandoArchivo === 'error-subida.png' ? 'Descargando...' : 'Captura Error'}
                          </button>
                          <button onClick={() => onDescargar(log._id, 'error-subida.html')}
                            disabled={descargandoArchivo === 'error-subida.html'}
                            className="inline-flex items-center gap-1.5 text-xs border border-orange-200 rounded-lg px-3 py-1.5 bg-orange-50 hover:bg-orange-100 text-orange-700 disabled:opacity-50 transition-colors">
                            🌐 {descargandoArchivo === 'error-subida.html' ? 'Descargando...' : 'HTML Error'}
                          </button>
                        </>
                      )}
                    </div>

                    {/* Log colapsable */}
                    <details className="group">
                      <summary className="cursor-pointer text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 select-none">
                        <span className="transition-transform group-open:rotate-90">▶</span> Ver log de ejecución
                      </summary>
                      <div className="mt-2 p-3 bg-gray-900 text-gray-300 rounded-lg text-xs font-mono max-h-56 overflow-y-auto whitespace-pre-wrap leading-relaxed">
                        {log.logEntries?.join('\n') || 'Sin logs'}
                      </div>
                    </details>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function DashboardEmpresaContabilidad({ empresa, anio: anioInicial, onClose }) {
  const [anio,     setAnio]     = useState(anioInicial || new Date().getFullYear());
  const [logs,     setLogs]     = useState([]);
  const [estados,  setEstados]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [descargandoArchivo, setDescargandoArchivo] = useState('');
  
  // UI de Multi-Mes / Ejecución
  const [showMulti, setShowMulti] = useState(false);
  const [mesesMulti, setMesesMulti] = useState([]);
  const [chkCompras, setChkCompras] = useState(true);
  const [chkVentas, setChkVentas] = useState(true);
  const [chkHonorarios, setChkHonorarios] = useState(false);
  const [chkBte, setChkBte] = useState(false);
  const [chkCentralizacion, setChkCentralizacion] = useState(false);
  const [chkSoloFetch, setChkSoloFetch] = useState(false);
  const [ejecutandoMulti, setEjecutandoMulti] = useState(false);

  const cargarDatos = useCallback(async (silencioso = false) => {
    if (!silencioso) setLoading(true);
    try {
      const id = (empresa._id || empresa).toString();
      const [logsRes, estadosRes] = await Promise.all([
        api.get('/contabilidad/logs', { params: { clienteId: id, anio, _t: Date.now() } }),
        api.get('/contabilidad/estados', { params: { clienteId: id, anio, _t: Date.now() } })
      ]);
      setLogs(logsRes.data || []);
      setEstados(estadosRes.data || []);
    } catch (err) {
      console.error(err);
    }
    if (!silencioso) setLoading(false);
  }, [empresa, anio]);

  useEffect(() => { 
    cargarDatos(); 
    const intervalId = setInterval(() => {
      cargarDatos(true);
    }, 3000);
    return () => clearInterval(intervalId);
  }, [cargarDatos]);

  const reejecutarProceso = async (mesNum, tipo) => {
    toast.info(`Iniciando proceso...`);
    try {
      await api.post('/contabilidad/ejecutar', {
        clienteId: empresa._id, mes: mesNum, anio, tipoDocumento: tipo, soloFetch: chkSoloFetch
      });
      toast.success('Proceso re-encolado exitosamente');
      cargarDatos();
    } catch (err) {
      toast.error(err.response?.data?.error || 'No se pudo iniciar el proceso');
    }
  };

  const ejecutarSeleccion = async () => {
    const tipos = [];
    if (chkCompras) tipos.push('compras');
    if (chkVentas) tipos.push('ventas');

    if (chkHonorarios) tipos.push('honorarios');
    if (chkBte) tipos.push('bte');
    if (chkCentralizacion && !chkCompras && !chkVentas) tipos.push('centralizacion');

    if (tipos.length === 0 || mesesMulti.length === 0) return;

    setEjecutandoMulti(true);
    toast.info(`Iniciando multi-mes para ${mesesMulti.length} meses...`);
    
    for (const t of tipos) {
      try {
        await api.post('/contabilidad/ejecutar-multimes', {
          clienteId: empresa._id,
          anio,
          meses: mesesMulti,
          tipoDocumento: t,
          soloFetch: chkSoloFetch
        });
      } catch (err) {
        console.error(`Error Multi-mes seleccion ${t}:`, err);
      }
    }
    
    toast.success('Lote encolado exitosamente');
    setShowMulti(false);
    cargarDatos();
    setEjecutandoMulti(false);
  };

  const ejecutarTodoMulti = async () => {
    if (mesesMulti.length === 0) return;
    setChkCompras(true); setChkVentas(true); setChkHonorarios(true); setChkBte(true); setChkCentralizacion(true);
    setEjecutandoMulti(true);
    toast.info(`Iniciando TODOS los procesos para ${mesesMulti.length} meses...`);
    const tiposTotales = ['compras', 'ventas', 'honorarios', 'bte'];
    for (const t of tiposTotales) {
      try {
        await api.post('/contabilidad/ejecutar-multimes', {
          clienteId: empresa._id, anio, meses: mesesMulti, tipoDocumento: t, soloFetch: chkSoloFetch
        });
      } catch (err) {
        console.error(`Error Multi-mes todo ${t}:`, err);
      }
    }
    toast.success('Lote completo encolado exitosamente');
    setShowMulti(false);
    cargarDatos();
    setEjecutandoMulti(false);
  };

  const cambiarEstadoLog = async (logId, nuevoEstado, tipoDocumento, mesNum) => {
    try {
      await api.put(`/contabilidad/logs/${logId}/estado`, { 
        estado: nuevoEstado,
        clienteId: (empresa._id || empresa).toString(),
        anio,
        mes: mesNum,
        tipoDocumento
      });
      toast.success(`Estado cambiado a ${nuevoEstado}`);
      cargarDatos(true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'No se pudo cambiar el estado');
    }
  };

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  const descargarArchivo = async (logId, fileName) => {
    if (!logId || !fileName) return;
    setDescargandoArchivo(fileName);
    try {
      const response = await api.get(
        `/contabilidad/logs/${logId}/archivos/${encodeURIComponent(fileName)}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: response.headers?.['content-type'] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo descargar el archivo');
    } finally {
      setDescargandoArchivo('');
    }
  };

  const anioActual = new Date().getFullYear();
  const años = Array.from({ length: 6 }, (_, i) => anioActual - i);

  // Construir datos por mes
  const datosMes = Array.from({ length: 12 }, (_, i) => {
    const mesNum    = i + 1;
    // Buscar log principal (hacemos let para poder asignar sintéticos si fueron borrados por limpieza)
    let logC      = logs.find(l => l.mes === mesNum && l.tipoDocumento === 'compras');
    let logV      = logs.find(l => l.mes === mesNum && l.tipoDocumento === 'ventas');
    let logCent   = logs.find(l => l.mes === mesNum && l.tipoDocumento === 'centralizacion');
    let logHono   = logs.find(l => l.mes === mesNum && l.tipoDocumento === 'honorarios');
    let logBte    = logs.find(l => l.mes === mesNum && l.tipoDocumento === 'bte');

    // Buscar estado oficial para sobreescribir el estado del log o crear log sintético
    const estadoDoc = estados.find(e => e.mes === mesNum);
    
    if (estadoDoc?.compras?.estado) {
      if (logC) logC.estado = estadoDoc.compras.estado;
      else logC = { estado: estadoDoc.compras.estado, _id: estadoDoc.compras.ultimoLogId };
    }
    if (estadoDoc?.ventas?.estado) {
      if (logV) logV.estado = estadoDoc.ventas.estado;
      else logV = { estado: estadoDoc.ventas.estado, _id: estadoDoc.ventas.ultimoLogId };
    }
    if (estadoDoc?.honorarios?.estado) {
      if (logHono) logHono.estado = estadoDoc.honorarios.estado;
      else logHono = { estado: estadoDoc.honorarios.estado, _id: estadoDoc.honorarios.ultimoLogId };
    }
    if (estadoDoc?.bte?.estado) {
      if (logBte) logBte.estado = estadoDoc.bte.estado;
      else logBte = { estado: estadoDoc.bte.estado, _id: estadoDoc.bte.ultimoLogId };
    }
    if (estadoDoc?.centralizacion?.estado) {
      if (logCent) logCent.estado = estadoDoc.centralizacion.estado;
      else logCent = { estado: estadoDoc.centralizacion.estado, _id: estadoDoc.centralizacion.ultimoLogId };
    }

    const totCompras = (logC?.resumenSii || []).reduce((s, r) => s + parseMonto(r.montoTotal), 0);
    const totVentas  = (logV?.resumenSii || []).reduce((s, r) => s + parseMonto(r.montoTotal), 0);
    const totHono    = logHono?.honorarios?.totalBruto || 0;
    const totBte     = logBte?.bte?.totalBruto || 0;
    const docsC      = (logC?.resumenSii || []).reduce((s, r) => s + parseDocs(r.totalDocumentos || r.cantidadDocumentos), 0);
    const docsV      = (logV?.resumenSii || []).reduce((s, r) => s + parseDocs(r.totalDocumentos || r.cantidadDocumentos), 0);
    return { mesNum, nombre: MESES_SHORT[mesNum], totCompras, totVentas, totHono, totBte, docsC, docsV, logC, logV, logCent, logHono, logBte };
  });

  // KPIs
  const totalVentasAnio  = datosMes.reduce((s, d) => s + d.totVentas, 0);
  const totalComprasAnio = datosMes.reduce((s, d) => s + d.totCompras, 0);
  const totalHonoAnio    = datosMes.reduce((s, d) => s + d.totHono, 0);
  const totalBteAnio     = datosMes.reduce((s, d) => s + d.totBte, 0);
  const mesesOK   = datosMes.filter(d => d.logC?.estado === 'completado' || d.logV?.estado === 'completado' || d.logHono?.estado === 'completado' || d.logBte?.estado === 'completado').length;
  const mesesError = datosMes.filter(d => d.logC?.estado === 'error' || d.logV?.estado === 'error' || d.logHono?.estado === 'error' || d.logBte?.estado === 'error').length;
  const mesesEjecutados = datosMes.filter(d => d.logC || d.logV || d.logHono || d.logBte).length;

  // Grafico
  const dataGrafico = datosMes.map(d => ({
    mes: d.nombre,
    Ventas: d.totVentas,
    Compras: d.totCompras,
    Honorarios: d.totHono,
    BTE: d.totBte,
  }));

  return (
    <div className="p-3 sm:p-4 md:p-6 max-w-7xl mx-auto min-h-screen">
      {/* ── Cabecera ── */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <button onClick={onClose}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors flex-shrink-0">
          ← Volver
        </button>
        <span className="text-gray-300 hidden sm:inline">/</span>
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-gray-800 truncate">{empresa.nombre}</h1>
          <p className="text-xs text-gray-400 font-mono">{empresa.rut}</p>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-shrink-0">
          <select value={anio} onChange={e => setAnio(Number(e.target.value))}
            className="border rounded-lg px-3 py-1.5 text-sm font-semibold text-gray-700 bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:outline-none">
            {años.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <button onClick={() => setShowMulti(p => !p)} title="Ejecutar Multi-Mes"
            className={`px-3 py-1.5 border rounded-lg text-sm font-semibold transition-colors ${showMulti ? 'bg-indigo-600 text-white border-indigo-700' : 'bg-white text-indigo-700 hover:bg-indigo-50 shadow-sm'}`}>
            ⚡ Ejecutar Batch
          </button>
          <button onClick={cargarDatos} title="Actualizar"
            className="px-3 py-1.5 border rounded-lg text-sm text-gray-500 hover:bg-gray-50 transition-colors bg-white shadow-sm">
            ↻
          </button>
        </div>
      </div>

      {showMulti && (
        <div className="mb-6 bg-white border border-indigo-200 rounded-xl p-4 shadow-sm animate__animated animate__fadeInDown animate__faster">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-bold text-indigo-800">Ejecución Multi-Mes / Batch</h3>
            <button onClick={() => setShowMulti(false)} className="text-xs text-gray-400 hover:text-gray-600">✕ Cerrar</button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-2">1. Seleccionar Meses</p>
              <div className="flex flex-wrap gap-1.5">
                {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                  <button key={m} onClick={() => setMesesMulti(p => p.includes(m) ? p.filter(x => x !== m) : [...p, m])}
                    className={`w-8 h-8 rounded text-xs font-bold transition-colors border ${mesesMulti.includes(m) ? 'bg-indigo-600 text-white border-indigo-700' : 'bg-gray-50 text-gray-600 hover:bg-indigo-50 border-gray-200'}`}>
                    {m}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-500 mb-2">2. Procesos a ejecutar</p>
              <div className="grid grid-cols-2 gap-2 bg-gray-50 p-2 rounded border border-gray-200">
                <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                  <input type="checkbox" checked={chkCompras} onChange={e => setChkCompras(e.target.checked)} className="rounded accent-emerald-600" /> Compras
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                  <input type="checkbox" checked={chkVentas} onChange={e => setChkVentas(e.target.checked)} className="rounded accent-emerald-600" /> Ventas
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                  <input type="checkbox" checked={chkHonorarios} onChange={e => setChkHonorarios(e.target.checked)} className="rounded accent-emerald-600" /> Honorarios
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                  <input type="checkbox" checked={chkBte} onChange={e => setChkBte(e.target.checked)} className="rounded accent-emerald-600" /> BTE
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                  <input type="checkbox" checked={chkCentralizacion} onChange={e => setChkCentralizacion(e.target.checked)} className="rounded accent-emerald-600" disabled={chkSoloFetch} /> Centralización
                </label>
              </div>
              <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded">
                <label className="flex items-center gap-1.5 cursor-pointer text-xs font-semibold text-blue-800">
                  <input type="checkbox" checked={chkSoloFetch} onChange={e => {
                    setChkSoloFetch(e.target.checked);
                    if (e.target.checked) setChkCentralizacion(false);
                  }} className="rounded accent-blue-600" />
                  ☁️ Solo Descargar desde SII (No subir a Inputsoft)
                </label>
                <p className="text-[10px] text-blue-600 mt-1 ml-5 leading-tight">
                  Descarga y procesa el CSV sin enviarlo a Inputsoft. Útil para obtener estadísticas sin afectar la contabilidad.
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-4 pt-4 border-t border-gray-100">
            <button onClick={ejecutarSeleccion} disabled={ejecutandoMulti || mesesMulti.length === 0 || (!chkCompras && !chkVentas && !chkHonorarios && !chkBte && !chkCentralizacion)}
              className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg shadow-sm transition-colors disabled:opacity-50">
              ▶ Ejecutar Selección ({mesesMulti.length} meses)
            </button>
            <button onClick={ejecutarTodoMulti} disabled={ejecutandoMulti || mesesMulti.length === 0}
              className="flex-1 py-2 bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-semibold rounded-lg shadow-sm transition-colors disabled:opacity-50">
              🌟 Ejecutar Todo ({mesesMulti.length} meses)
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-32 text-center text-gray-400">
          <div className="text-4xl mb-3">⟳</div>
          <p>Cargando información de {anio}...</p>
        </div>
      ) : (
        <div className="space-y-6">

          {/* ── KPI Cards ── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Total Ventas Año', value: fmtCLP(totalVentasAnio), color: 'blue', icon: '↑' },
              { label: 'Total Compras Año', value: fmtCLP(totalComprasAnio), color: 'green', icon: '↓' },
              { label: 'Total Honorarios', value: fmtCLP(totalHonoAnio), color: 'purple', icon: '📄' },
              { label: 'Total BTE', value: fmtCLP(totalBteAnio), color: 'teal', icon: '📄' },
              { label: 'Meses con Error', value: mesesError, color: mesesError > 0 ? 'red' : 'green', icon: mesesError > 0 ? '⚠' : '✓' },
            ].map((kpi, i) => (
              <div key={i} className="bg-white border rounded-xl p-4 shadow-sm">
                <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">{kpi.label}</div>
                <div className={`text-xl font-bold ${
                  kpi.color === 'blue' ? 'text-blue-700' :
                  kpi.color === 'green' ? 'text-green-700' :
                  kpi.color === 'purple' ? 'text-purple-700' :
                  kpi.color === 'teal' ? 'text-teal-700' :
                  kpi.color === 'red' ? 'text-red-600' : 'text-gray-800'
                }`}>{kpi.value}</div>
              </div>
            ))}
          </div>

          {/* ── Gráfico de Barras ── */}
          <div className="bg-white border rounded-xl p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-700 mb-4">Evolución Monetaria {anio} (CLP)</h2>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dataGrafico} margin={{ top: 5, right: 10, left: 30, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="mes" fontSize={12} tick={{ fill: '#6b7280' }} />
                  <YAxis tickFormatter={v => '$' + (v / 1000000).toFixed(1) + 'M'} fontSize={11} tick={{ fill: '#9ca3af' }} />
                  <Tooltip
                    formatter={(val) => fmtCLP(val)}
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Ventas"  fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Compras" fill="#10b981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Honorarios" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="BTE" fill="#14b8a6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ── Tabla de Meses ── */}
          <div>
            <div className="flex flex-wrap items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-700">Detalle por Mes</h2>
              <label className="flex items-center gap-1.5 cursor-pointer bg-slate-100 hover:bg-slate-200 border border-slate-300 px-3 py-1.5 rounded-full transition-colors">
                <input type="checkbox" checked={chkSoloFetch} onChange={e => {
                    setChkSoloFetch(e.target.checked);
                    if (e.target.checked) setChkCentralizacion(false);
                  }} className="accent-slate-600" />
                <span className="text-xs font-semibold text-slate-700">☁️ Solo Descargar (Aplica a Re-ejecutar)</span>
              </label>
            </div>
            <div className="space-y-2">
              {datosMes.map(d => (
                <FilaMes
                  key={d.mesNum}
                  mesNum={d.mesNum}
                  logCompras={d.logC}
                  logVentas={d.logV}
                  logCent={d.logCent}
                  logHono={d.logHono}
                  logBte={d.logBte}
                  descargandoArchivo={descargandoArchivo}
                  onDescargar={descargarArchivo}
                  onReejecutar={reejecutarProceso}
                  onCambiarEstado={cambiarEstadoLog}
                />
              ))}
            </div>
          </div>

        </div>
      )}
    </div>
  );
}
